import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import { canvasAabbForLayoutShape } from './layout-shape-aabb'

const ALIGN_EPS = 1e-6

export type BuildAlignOptions = {
  referenceShapeId?: string
  minSelectionForReference?: number
}

type AxisSpan = { shape: ShapeData; min: number; max: number }

type AxisPosition = { shape: ShapeData; position: number }

function axisSpans(
  shapes: ShapeData[],
  getBounds: (id: string) => ShapeBounds | undefined,
  axis: 'x' | 'y'
): AxisSpan[] {
  return shapes.map((shape) => {
    const abb = canvasAabbForLayoutShape(shape, getBounds(shape.id))

    return axis === 'x'
      ? { shape, min: abb.minX, max: abb.maxX }
      : { shape, min: abb.minY, max: abb.maxY }
  })
}

/** The key object's id (Alt+click focus) when it applies to this selection, else `undefined`. */
function resolveReferenceShapeId(
  shapes: ShapeData[],
  options: BuildAlignOptions | undefined
): string | undefined {
  const minN = options?.minSelectionForReference ?? 2
  const refId = options?.referenceShapeId
  const useReference = refId != null && shapes.length >= minN && shapes.some((s) => s.id === refId)

  return useReference ? refId : undefined
}

/** Moves every unlocked shape along `axis` so its `position` lands on `target`. */
function toMoveItems(
  entries: AxisPosition[],
  axis: 'x' | 'y',
  target: number
): BatchShapeTransformItem[] {
  const items: BatchShapeTransformItem[] = []

  for (const { shape, position } of entries) {
    if (shape.locked) {
      continue
    }

    const delta = target - position

    if (Math.abs(delta) < ALIGN_EPS) {
      continue
    }

    items.push({
      shapeId: shape.id,
      before: { x: shape.x, y: shape.y },
      after: {
        x: axis === 'x' ? shape.x + delta : shape.x,
        y: axis === 'y' ? shape.y + delta : shape.y,
      },
    })
  }

  return items
}

/**
 * Align to the minimum canvas edge (top = min Y, left = min X) or the maximum canvas edge
 * (bottom = max Y, right = max X), on the same visual-bounds basis for both. Locked shapes define
 * the target edge when present. With referenceShapeId and 2+ shapes, the target edge is the key
 * object's edge (Alt+click focus).
 */
export function buildAlignEdgeItems(
  shapes: ShapeData[],
  getBounds: (id: string) => ShapeBounds | undefined,
  axis: 'x' | 'y',
  side: 'min' | 'max',
  options?: BuildAlignOptions
): BatchShapeTransformItem[] {
  const withEdges = axisSpans(shapes, getBounds, axis).map(({ shape, min, max }) => ({
    shape,
    position: side === 'min' ? min : max,
  }))
  const refId = resolveReferenceShapeId(shapes, options)
  const pick = side === 'min' ? Math.min : Math.max

  let target: number

  if (refId !== undefined) {
    const refEntry = withEdges.find((x) => x.shape.id === refId)!

    target = refEntry.position
  } else {
    const lockedEdges = withEdges.filter((x) => x.shape.locked === true)

    target =
      lockedEdges.length > 0
        ? pick(...lockedEdges.map((x) => x.position))
        : pick(...withEdges.map((x) => x.position))
  }

  return toMoveItems(withEdges, axis, target)
}

/**
 * Align centers: move each object's center on `axis` to the midline of the selection bounds
 * (union of canvas AABBs) — 'y' is vertical align center, 'x' is horizontal align center. Locked
 * shapes define that line when present. With referenceShapeId and 2+ shapes, target is the key
 * object's center (Alt+click focus).
 */
export function buildAlignCenterItems(
  shapes: ShapeData[],
  getBounds: (id: string) => ShapeBounds | undefined,
  axis: 'x' | 'y',
  options?: BuildAlignOptions
): BatchShapeTransformItem[] {
  const entries = axisSpans(shapes, getBounds, axis).map(({ shape, min, max }) => ({
    shape,
    min,
    max,
    position: (min + max) / 2,
  }))
  const refId = resolveReferenceShapeId(shapes, options)

  let targetCenter: number

  if (refId !== undefined) {
    const refEntry = entries.find((e) => e.shape.id === refId)

    if (!refEntry) {
      return []
    }

    targetCenter = refEntry.position
  } else {
    const lockedEntries = entries.filter((e) => e.shape.locked === true)
    const forUnion = lockedEntries.length > 0 ? lockedEntries : entries

    let unionMin = Infinity
    let unionMax = -Infinity

    for (const e of forUnion) {
      if (e.min < unionMin) unionMin = e.min
      if (e.max > unionMax) unionMax = e.max
    }

    if (!Number.isFinite(unionMin) || !Number.isFinite(unionMax)) {
      return []
    }

    targetCenter = (unionMin + unionMax) / 2
  }

  return toMoveItems(entries, axis, targetCenter)
}
